import React, { useCallback, useEffect } from 'react'
import { MenuItem, Select, SelectChangeEvent, styled, Tooltip, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { IFunctionComponentInstance, ITextStyles, TextAlignment } from 'src/types/report-designer'
import { FunctionType } from 'src/enums'
import { AppTextField } from '../../AppTextField'
import { IFormSchemaColumn } from 'src/generated-types/form-schema'
import {
  AlignmentToggleGroup,
  BoldToggleRow,
  ColorSwatchField,
  FontSizeSelect,
  FontFamilySelect,
  LineHeightSelect,
  LetterSpacingSelect,
} from './PropertyComponents'
import {
  FieldSection,
  getTextFontColors,
  PropertiesWrapper,
  PropertyLabel,
  SectionHeading,
  StyledDivider,
} from './PropertiesShared.styles'
import { WarningAmber } from '@mui/icons-material'

export interface IFunctionComponentPropertiesProps {
  instance: IFunctionComponentInstance
  onChange: (updated: IFunctionComponentInstance) => void
  columns?: IFormSchemaColumn[]
  isColumnsLoading: boolean
}

export const FunctionComponentProperties: React.FC<IFunctionComponentPropertiesProps> = ({
  instance,
  onChange,
  columns,
  isColumnsLoading,
}) => {
  const theme = useTheme()
  const { t: translate } = useTranslation(['dynamic-report'])
  const FONT_COLORS = getTextFontColors(theme)
  const [selectedColumn, setSelectedColumn] = React.useState<IFormSchemaColumn | null>(
    columns?.find(c => c.identifier === instance.selectedColumnId) ?? null,
  )

  useEffect(() => {
    const column = instance.selectedColumnId ? columns?.find(c => c.identifier === instance.selectedColumnId) : null
    setSelectedColumn(column ?? null)
  }, [instance.selectedColumnId, columns])

  const handleLabelChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...instance, label: e.target.value })
    },
    [instance, onChange],
  )

  const handleFunctionTypeChange = useCallback(
    (e: SelectChangeEvent<string | null>) => {
      const value = e.target.value === '' ? null : (Number(e.target.value) as FunctionType)
      onChange({ ...instance, functionType: value, selectedColumnId: null, selectedColumnName: null })
    },
    [instance, onChange],
  )

  const handleColumnChange = useCallback(
    (e: SelectChangeEvent<string | null>) => {
      const value = e.target.value === '' ? null : e.target.value
      const column = value ? columns?.find(c => c.identifier === value) : null
      onChange({ ...instance, selectedColumnId: value, selectedColumnName: column?.name ?? null })
    },
    [instance, onChange, columns],
  )

  const handleStyleChange = useCallback(
    <K extends keyof ITextStyles>(key: K, value: ITextStyles[K]) => {
      onChange({ ...instance, styles: { ...instance.styles, [key]: value } })
    },
    [instance, onChange],
  )

  const handleAlignmentChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, value: TextAlignment | null) => {
      if (value) handleStyleChange('alignment', value)
    },
    [handleStyleChange],
  )

  return (
    <PropertiesWrapper>
      <FieldSection>
        <PropertyLabel>{translate('label', { ns: 'dynamic-report' })}</PropertyLabel>
        <AppTextField
          size="small"
          fullWidth
          value={instance.label}
          onChange={handleLabelChange}
          placeholder={translate('label-placeholder', { ns: 'dynamic-report' })}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('function-type', { ns: 'dynamic-report' })}</PropertyLabel>
        <Select
          size="small"
          fullWidth
          value={instance.functionType != null ? String(instance.functionType) : ''}
          onChange={handleFunctionTypeChange}
          displayEmpty
          renderValue={selected => {
            if (selected === '') {
              return (
                <PlaceholderText>
                  {translate('select-function-type-placeholder', { ns: 'dynamic-report' })}
                </PlaceholderText>
              )
            }
            return FunctionType[Number(selected) as unknown as FunctionType]
          }}
        >
          {(Object.entries(FunctionType).filter(([k]) => isNaN(Number(k))) as [string, FunctionType][]).map(
            ([name, value]) => (
              <MenuItem key={value} value={String(value)}>
                {name}
              </MenuItem>
            ),
          )}
        </Select>
      </FieldSection>

      <FieldSection>
        <PropertyLabel display="flex" alignContent="center" alignItems="center">
          {selectedColumn === null && instance.selectedColumnId != null && (
            <Tooltip title={translate('deleted-selected-column-tooltip', { ns: 'dynamic-report' }) as string}>
              <WarningAmber sx={{ fontSize: '14px', color: 'error.main', marginRight: '8px' }} />
            </Tooltip>
          )}
          {translate('selected-column', { ns: 'dynamic-report' })}
        </PropertyLabel>
        <Select
          size="small"
          fullWidth
          value={instance.selectedColumnId ?? ''}
          onChange={handleColumnChange}
          disabled={isColumnsLoading || !columns || columns.length === 0}
          displayEmpty
          renderValue={selected => {
            if (selected === '') {
              return (
                <PlaceholderText>{translate('select-column-placeholder', { ns: 'dynamic-report' })}</PlaceholderText>
              )
            }
            const column = columns?.find(c => c.identifier === selected)
            return column?.name ?? instance.selectedColumnName ?? String(selected)
          }}
        >
          {columns?.map(column => (
            <MenuItem key={column.identifier} value={column.identifier}>
              {column.name}
            </MenuItem>
          ))}
        </Select>
      </FieldSection>

      <StyledDivider />

      <SectionHeading>{translate('styles', { ns: 'dynamic-report' })}</SectionHeading>

      <FieldSection>
        <PropertyLabel>{translate('font-color', { ns: 'dynamic-report' })}</PropertyLabel>
        <ColorSwatchField
          colors={FONT_COLORS}
          selectedColor={instance.styles.fontColor}
          onColorChange={color => handleStyleChange('fontColor', color)}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('font-size', { ns: 'dynamic-report' })}</PropertyLabel>
        <FontSizeSelect value={instance.styles.fontSize} onChange={size => handleStyleChange('fontSize', size)} />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('font-family', { ns: 'dynamic-report' })}</PropertyLabel>
        <FontFamilySelect
          value={instance.styles.fontFamily}
          onChange={family => handleStyleChange('fontFamily', family)}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('alignment', { ns: 'dynamic-report' })}</PropertyLabel>
        <AlignmentToggleGroup value={instance.styles.alignment} onChange={handleAlignmentChange} />
      </FieldSection>

      <BoldToggleRow
        label={translate('bold', { ns: 'dynamic-report' })}
        checked={instance.styles.bold}
        onChange={checked => handleStyleChange('bold', checked)}
      />

      <FieldSection>
        <PropertyLabel>{translate('line-height', { ns: 'dynamic-report' })}</PropertyLabel>
        <LineHeightSelect
          value={instance.styles.lineHeight ?? 1.4}
          onChange={val => handleStyleChange('lineHeight', val)}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('letter-spacing', { ns: 'dynamic-report' })}</PropertyLabel>
        <LetterSpacingSelect
          value={instance.styles.letterSpacing ?? 0}
          onChange={val => handleStyleChange('letterSpacing', val)}
        />
      </FieldSection>
    </PropertiesWrapper>
  )
}

const PlaceholderText = styled('span')(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontStyle: 'italic',
}))
